import { CommonModule } from '@angular/common';
import { Component, forwardRef, Inject, Injector, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormControlDirective, FormControlName, FormGroupDirective, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validator, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { ISelection } from 'src/app/interfaces/ISelection';
import { IRetrieveParam } from 'src/app/interfaces/ITable';
import { MaterialModule } from 'src/app/material.module';
import { SelectionConfig } from 'src/app/types/Select';

@Component({
    selector: 'app-form-select',
    imports: [MaterialModule, CommonModule, TranslateModule],
    templateUrl: './form-select.component.html',
    styleUrl: './form-select.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormSelectComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FormSelectComponent),
            multi: true,
        },
    ]
})
export class FormSelectComponent implements ControlValueAccessor, Validator, OnChanges, OnInit {
  @Input() fetchData?: (params: IRetrieveParam) => Observable<any>;
  @Input() mapApiResponse?: (response: any) => any;
  @Input() dataSource: ISelection[] = [];
  @Input() params: IRetrieveParam = { page: 1, pageSize: 1000, sortText: '', globalSearch: '' };
  @Input() selectionConfig: SelectionConfig;
  @Input() placeholder: string = ''
  formControl: FormControl;
  value: any;
  disabled = false;
  unsubscribe: Subscription;
  groups: string[] = [];
  loading: boolean = false;
  control: AbstractControl;
  isRequired: boolean = false;
  // Angular form callback function
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(@Inject(Injector) private injector: Injector) {}
  
  ngOnInit(): void {
    this.setFormControl();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes['fetchData']) {
      this.retrieveData();
    }
    if(changes['dataSource']) {
      this.groups = this.getGroup(this.dataSource);
    }
  }

  writeValue(value: any): void {
    if(value) {
      switch(typeof value) { 
        case 'object': { 
          // for default value is array, use with multiple selected
          this.value = value.map((e: any) => e[this.selectionConfig.key ?? ''] ?? e);
          break; 
        } 
        default: {
          // for default value is guid and number
          this.value = value;
           break; 
        } 
      } 
      if(this.value != value) {
        // handle form value
        this.control.setValue(this.value)
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control;
    this.isRequired = control.hasValidator(Validators.required) || false; // check form required
    if(this.isRequired) {
      return (this.value ?? "").toString().length > 0 ? null : { required: true };
    }
    return null;
  }

  onSelectChange(value: any): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  retrieveData() {
    if (this.fetchData) {
      const reqParams = { ...this.params };
      this.dataSource = [];
      this.unsubscribe?.unsubscribe();
      this.loading = true;
      this.unsubscribe = this.fetchData(reqParams).subscribe((response) => {
        let dataMapApi;
        if (this.mapApiResponse) {
          dataMapApi = this.mapApiResponse(response.data ?? response);
        }
        this.dataSource = (dataMapApi ?? response.data ?? response).map((e: any) => ({
          value: e[this.selectionConfig.key || ''],
          text: e[this.selectionConfig.title || ''],
          group: e[this.selectionConfig.group || ''],
          disabled: typeof this.selectionConfig?.disabled === 'boolean' ? this.selectionConfig?.disabled : this.selectionConfig.disabled && this.selectionConfig.disabled(e) ,
        }));
        this.groups = this.getGroup(this.dataSource);
        this.loading = false;
      });
    }
  }

  getGroup(data: any): string[] {
    return data.reduce((result: string[], currentValue: ISelection) => {
      if (currentValue?.group && !result.includes(currentValue?.group)) {
        result.push(currentValue.group);
      }
      return result;
    }, []);
  }

  getGroupData(group: string): ISelection[] {
    return this.dataSource.filter((item: ISelection) => item.group === group);
  }

  hasError(): boolean {
    return this?.control?.invalid && this?.control?.touched;
  }

  setFormControl = () => {
    try {
      const formControl = this.injector.get(NgControl);
      switch (formControl.constructor) {
        case FormControlName:
          this.control = this.injector.get(FormGroupDirective).getControl(formControl as FormControlName);
          break;
        default:
          this.control = (formControl as FormControlDirective).form as FormControl;
          break;
      }
    } catch (err) {
      // this.control = new FormControl();
    }
  }
}
